import { request } from "$/lib/api"
import {
  CatalogProductDefault,
  CatalogProductDto,
  CatalogProductList,
  Product,
  Route,
  ServiceAction,
  UpsertCatalogProduct,
  populateRoute,
} from "@repo/common"
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"

interface SyncStatusDto {
  status: "syncing" | "completed" | "failed" | "idle"
  syncStartedAt: string | null
  syncCompletedAt: string | null
  syncError: string | null
}

interface ProductListParams {
  page: number
  size: number
  search?: string
  sortBy?: string
  sortOrder?: "asc" | "desc"
}

async function fetchCatalogProducts(
  storeId: string,
  params: ProductListParams,
): Promise<CatalogProductList> {
  const queryParams: Record<string, string | number> = {
    page: params.page,
    size: params.size,
  }

  if (params.search) {
    queryParams.search = params.search
  }

  if (params.sortBy) {
    queryParams.sortBy = params.sortBy
  }

  if (params.sortOrder) {
    queryParams.sortOrder = params.sortOrder
  }

  return request<CatalogProductList>({
    url: populateRoute(Route.CATALOG_PRODUCT__LIST, storeId),
    params: queryParams,
    method: "GET",
  })
}

const catalogProductQueries = {
  all: () => ["catalog-products"],
  lists: () => [...catalogProductQueries.all(), "list"],
  list: (storeId: string, params: ProductListParams) =>
    queryOptions({
      queryKey: [...catalogProductQueries.lists(), storeId, params],
      queryFn: () => fetchCatalogProducts(storeId, params),
    }),
  forPartAction: (storeId: string, partId: string, action: ServiceAction) =>
    queryOptions({
      queryKey: [
        ...catalogProductQueries.all(),
        "part-action",
        storeId,
        partId,
        action,
      ],
      queryFn: () =>
        request<Product[]>({
          url: populateRoute(
            Route.CATALOG_PRODUCT__FOR_PART_ACTION,
            storeId,
            partId,
            action,
          ),
          method: "GET",
        }),
    }),
  defaults: (storeId: string) =>
    queryOptions({
      queryKey: [...catalogProductQueries.all(), "defaults", storeId],
      queryFn: () =>
        request<CatalogProductDefault[]>({
          url: populateRoute(Route.CATALOG_PRODUCT__DEFAULTS, storeId),
          method: "GET",
        }),
    }),
  syncStatus: (storeId: string) =>
    queryOptions({
      queryKey: [...catalogProductQueries.all(), "sync-status", storeId],
      queryFn: () =>
        request<SyncStatusDto>({
          url: populateRoute(Route.SPECTER_PRODUCT__SYNC_STATUS, storeId),
          method: "GET",
        }),
    }),
}

export default catalogProductQueries

/**
 * Products added automatically for a part + action (there can be several).
 * Keyed as `${partId}:${action}` — build keys with `defaultProductKey`.
 */
export type DefaultProductsByPartAction = Map<string, Product[]>

export const defaultProductKey = (partId: string, action: ServiceAction) =>
  `${partId}:${action}`

export const toDefaultProductsByPartAction = (
  defaults: CatalogProductDefault[],
): DefaultProductsByPartAction => {
  const byKey: DefaultProductsByPartAction = new Map()
  for (const d of defaults) {
    const key = defaultProductKey(d.partId, d.action)
    byKey.set(key, [...(byKey.get(key) ?? []), d.product])
  }
  return byKey
}

export function useSyncSpecterProducts(storeId: string) {
  return useMutation({
    mutationFn: () =>
      request<SyncStatusDto>({
        url: populateRoute(Route.SPECTER_PRODUCT__SYNC, storeId),
        method: "POST",
      }),
  })
}

export function useCreateCatalogProduct(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (data: UpsertCatalogProduct) =>
      request<CatalogProductDto>({
        url: populateRoute(Route.CATALOG_PRODUCT__LIST, storeId),
        method: "POST",
        data,
      }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: catalogProductQueries.all() }),
  })
}

export function useUpdateCatalogProduct(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpsertCatalogProduct }) =>
      request<CatalogProductDto>({
        url: populateRoute(Route.CATALOG_PRODUCT__FIND_ONE, storeId, id),
        method: "PUT",
        data,
      }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: catalogProductQueries.all() }),
  })
}

export function useDeleteCatalogProduct(storeId: string) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      request<void>({
        url: populateRoute(Route.CATALOG_PRODUCT__FIND_ONE, storeId, id),
        method: "DELETE",
      }),
    onSettled: () =>
      queryClient.invalidateQueries({ queryKey: catalogProductQueries.all() }),
  })
}
